import { Injectable } from '@angular/core';
import { CONSTANTS } from 'src/app/core/constants/constants';
import { CALC_TYPES } from 'src/app/core/enums/calc-types';
import { LineGraphicProperties } from 'src/app/core/models/3D/graphic-properties/line-graphic-properties.model';
import { Line } from 'src/app/core/models/line.model';
import { NodeType } from 'src/app/core/models/load/node/node-type.enum';
import { MeshInstructions } from 'src/app/core/models/mesh-instructions';
import { SegmentConfig } from 'src/app/core/models/segment-config.model';
import { SegmentMesh } from 'src/app/core/models/segment-mesh.model';
import { Simulation } from 'src/app/core/models/simulation.model';
import { Clone } from 'src/app/shared/utils/clone';
import { IsEqual } from 'src/app/shared/utils/is-equal';
import { ObjectState } from 'src/app/shared/utils/object-state';
import { RemoveAllIds } from 'src/app/shared/utils/remove-all-ids/remove-all-ids';
import { DeleteRequestAnswer } from 'src/app/shared/utils/simulation-manager-aux/delete-request-answer';
import { Color } from 'three';
import { DataSharingService } from '../../../data-sharing/data-sharing.service';
import { EnvironmentPropsService } from '../../../environment-props/environment-props.service';
import { AnalysisService } from '../../load/analysis/analysis.service';
import { NodesService } from '../../load/nodes/nodes.service';
import { LineResultsRequestersService } from '../../results/line-results-requesters/line-results-requesters.service';
import { ResultsService } from '../../results/results.service';
import { SimulationManagerService } from '../../simulation-manager.service';
import { MapsService } from '../maps/maps.service';
import { ReferenceSystemService } from '../reference-system/reference-system.service';
import { TitleCopyService } from '../title-copy/title-copy.service';

@Injectable({
  providedIn: 'root',
})
export class LineService {
  constructor(
    private simulationService: SimulationManagerService,
    private dataSharingService: DataSharingService,
    private mapsService: MapsService,
    private titleCopyService: TitleCopyService,
    private resultsService: ResultsService,
    private lineResultsRequestersService: LineResultsRequestersService,
    private environmentPropsService: EnvironmentPropsService,
    private nodesService: NodesService,
    private referenceSystemService: ReferenceSystemService,
    private analysisService: AnalysisService,
  ) {}

  get simulation(): Simulation {
    return this.simulationService.simulation;
  }

  getById(lineId: number): Line {
    const answer = this.simulation.line?.find((line) => line.internalId === lineId);
    return answer || null;
  }

  getNew(): Line {
    if (!this.simulation.results?.length) {
      this.resultsService.get();
    }
    this.create();
    return this.simulation.line[this.simulation.line.length - 1];
  }

  createEmptySegConfig(numSegments: number): SegmentConfig[] {
    const segConf: SegmentConfig[] = [];
    for (let i = 0; i < numSegments; i++) {
      const nConf: SegmentConfig = new SegmentConfig();
      nConf.flexjointBeginId = -1;
      nConf.stiffenerBeginId = -1;
      nConf.flexjointEndId = -1;
      nConf.stiffenerEndId = -1;
      nConf.orientation = CONSTANTS.SEGMENT_ORIENTATION_TYPES.NONE;
      nConf.floaterConfig = [];
      nConf.strakeConfig = [];
      segConf.push(nConf);
    }
    return segConf;
  }

  create(): void {
    const newLine: Line = new Line();
    const id = this.simulationService.getNewId();

    newLine.internalId = id;
    newLine.connectionId = -1;
    newLine.fluidFunctionId = -1;
    newLine.azimuth = 0;
    newLine.calcType = CALC_TYPES.PROJECTION;
    newLine.title = `Line-${id}`;
    newLine.segmentMesh = [new SegmentMesh()];
    newLine.segmentMaterialId = [-1];
    newLine.segmentSoilId = [-1];
    newLine.segConfig = this.createEmptySegConfig(1);

    if (this.environmentPropsService.isAnflex) {
      const connection = this.simulation.connection[0];
      newLine.connectionId = connection ? connection.internalId : -1;
      newLine.needsUpdate = true;
      newLine.graphicProperties = new LineGraphicProperties({
        width: 2,
        color: new Color(0xffffff),
      });
      this.nodesService.create(newLine, NodeType.ANCHOR);
      this.analysisService.createFluidLoad(newLine);
    }

    if (this.simulation.line) {
      this.simulation.line.push(newLine);
    } else {
      this.simulation.line = [newLine];
    }

    this.mapsService.saveInAllMaps(CONSTANTS.LINE, newLine);
    this.simulationService.changeSimulation();
  }

  duplicate(originalId: number): Line {
    let originalIndex = 0;
    const originalObject = this.simulation.line.find(({ internalId }, index) => {
      originalIndex = index + 1;
      return internalId === originalId;
    });

    const originalCopy = Clone(originalObject);
    const newObject: Line = new Line();
    const newId = this.simulationService.getNewId();
    Object.assign(newObject, originalCopy);
    RemoveAllIds(newObject);
    newObject.internalId = newId;
    newObject.title = this.titleCopyService.getCopyFromTitle(originalObject.title);

    if (this.environmentPropsService.isAnflex) {
      newObject.needsUpdate = true;
      newObject.graphicProperties = new LineGraphicProperties({
        width: 2,
        color: new Color(0xffffff),
      });
      this.nodesService.create(newObject, NodeType.ANCHOR);
      this.analysisService.createFluidLoad(newObject);
    }

    this.mapsService.saveInAllMaps(CONSTANTS.LINE, newObject);
    this.simulation.line.splice(originalIndex, 0, newObject);
    this.simulationService.changeSimulation();
    this.dataSharingService.updateCatenary$.next();

    return newObject;
  }

  saveLabel(id: number, payload: { title: string }): void {
    const line = this.getById(id);
    if (line && line.title !== payload.title) {
      Object.assign(line, payload);
      this.propagateChanges(line);
    }
  }

  save(id: number, payload: any): void {
    const line = this.getById(id);
    if (!line || !payload) {
      return;
    }
    this.dataSharingService.loading$.next(true);

    const originalGraphicProps = line.graphicProperties;
    delete line.graphicProperties;
    const tempLine = Object.assign(Clone(line), {
      connectionId: payload.connectionId,
      catenaryAnchorId: payload.catenaryAnchorId,
      calcType: payload.calcType,
      projection: payload.projection,
      angle: payload.angle,
      tension: payload.tension,
      verticalTension: payload.verticalTension,
      topPressure: payload.topPressure,
      horizontalTension: payload.horizontalTension,
      azimuth: payload.azimuth,
      segment: payload.segment,
      fluidMinimum: payload.fluidMinimum,
      fluidMostProbable: payload.fluidMostProbable,
      fluidMaximum: payload.fluidMaximum,
      fluidFunctionId: payload.fluidFunctionId,
    });

    this.forceMeshNewLength(tempLine, payload);

    if (payload.segmentsTableRows) {
      this.saveSegments(payload.segmentsTableRows, tempLine);
    }

    if (IsEqual(line, tempLine)) {
      line.graphicProperties = originalGraphicProps;
      this.dataSharingService.loading$.next(false);
      return;
    }

    Object.assign(line, tempLine);
    line.graphicProperties = originalGraphicProps;
    delete (line as any).segmentsTableRows;
    this.propagateChanges(line);
    this.dataSharingService.loading$.next(false);
  }

  saveSegments(rows: any[], line: Line) {
    this.startEmptySegments(rows, line);
    this.fillLineSegments(rows, line);
  }

  startEmptySegments(segments: any[], line: Line) {
    const numOfSegments = segments.length;
    line.segmentMaterialId = Array<number>(numOfSegments).fill(-1);
    line.segmentSoilId = Array<number>(numOfSegments).fill(-1);
    line.segConfig = this.createEmptySegConfig(numOfSegments);
    line.segFluidSpecificWeight = Array<number>(numOfSegments).fill(0);
    line.segFluidAnnularWeight = Array<number>(numOfSegments).fill(0);
    line.segmentMesh = [];
  }

  fillLineSegments(rows: any[], line: Line) {
    const segments = rows;

    segments.forEach((value, index) => {
      const { segmentMaterial, segmentSoil, segConfig, length } = value;

      const parsedLength = isNaN(+length) ? 0 : +length;

      line.segmentMaterialId[index] = segmentMaterial;
      line.segmentSoilId[index] = segmentSoil;
      line.segConfig[index].orientation = segConfig.orientation;
      line.segConfig[index].floaterConfig =
        segConfig.floaterConfig || line.segConfig[index].floaterConfig;
      line.segConfig[index].strakeConfig =
        segConfig.strakeConfig || line.segConfig[index].strakeConfig;
      line.segConfig[index].flexjointEndId = segConfig.flexjointEndId || -1;
      line.segConfig[index].flexjointBeginId = segConfig.flexjointBeginId || -1;
      line.segConfig[index].stiffenerEndId = segConfig.stiffenerEndId || -1;
      line.segConfig[index].stiffenerBeginId = segConfig.stiffenerBeginId || -1;

      this.saveMesh(parsedLength, line);
    });
  }

  saveMesh(length: number, line: Line): void {
    const elementSize = ~~length === 0 ? length : length / ~~length;
    const mesh = new SegmentMesh();
    mesh.l = length;
    mesh.l0 = elementSize;
    mesh.l1 = elementSize;
    line.segmentMesh.push(mesh);
  }

  forceMeshNewLength(line: Line, payload: any): void {
    const positions = line.meshInstructions?.position;
    if (!positions) {
      line.meshInstructions = null;
      return;
    }
    const lastPositionIndex = positions.length - 1;
    positions[lastPositionIndex] = payload.segmentsTableRows.reduce(
      (sum, row) => row.length + sum,
      0,
    );
    positions[0] = 0;
  }

  saveMeshInstructions(lineId: number, rows: any[]): Line {
    const line = this.getById(lineId);

    if (rows.length === 0) {
      line.meshInstructions = null;
      return;
    }

    const meshInstructions = new MeshInstructions();

    rows.forEach((row) => {
      meshInstructions.length.push(row.length);
      meshInstructions.position.push(row.position);
    });

    Object.assign(line, { meshInstructions });
    delete (line as any).segmentsTableRows;
    this.propagateChanges(line);
    return line;
  }

  propagateChanges(line: Line): void {
    this.mapsService.updateObject(line);

    if (this.environmentPropsService.isAnflex) {
      this.nodesService.refreshReferences(line, NodeType.ANCHOR);
      this.nodesService.refreshSegmentNodes(line);
      this.lineResultsRequestersService.updateInstructionsByLineIds([line.internalId]);
      line.needsUpdate = true;
      line.graphicProperties =
        line.graphicProperties ||
        new LineGraphicProperties({ width: 2, color: new Color(0xffffff) });
    }

    this.simulationService.changeSimulation(false);
    this.dataSharingService.updateCatenary$.next();
  }

  delete(id: number): DeleteRequestAnswer {
    const lineToDelete = this.simulation.line.find((l) => l.internalId === id);
    lineToDelete?.graphicProperties?.delete();
    this.simulation.line = this.simulation.line.filter((line) => line.internalId !== id);
    this.mapsService.deleteFromMaps(id);
    if (this.environmentPropsService.isAnflex) {
      this.lineResultsRequestersService.deleteRequestersByLine(id);
      this.nodesService.deleteByReferenceId(id);
      this.referenceSystemService.resetByLineId(id);
      this.analysisService.deleteLineReferences(id);
    }
    this.simulationService.changeSimulation();
    this.dataSharingService.updateCatenary$.next();
    return new DeleteRequestAnswer(ObjectState.objectWasDeleted, null);
  }

  resetDatasheetLine(): void {
    const line = this.simulation.line[0];
    this.resetLine(line);
    this.mapsService.updateObject(line);
    this.simulation.line = [line];
    this.simulationService.changeSimulation(false);
    this.dataSharingService.updateCatenary$.next();
  }

  resetLine(line: Line): void {
    line.projection = 0;
    line.angle = 0;
    line.tension = 0;
    line.force = 0;
    line.azimuth = 0;
    line.fluidDatasheet = null;
    line.meshInstructions = null;
    line.connectionId = -1;
    line.fluidFunctionId = -1;
    line.catenaryAnchorId = -1;
    line.segFluidSpecificWeight = [];
    line.segFluidAnnularWeight = [];
    line.segmentBuoyId = [];
    line.segmentTendonId = [];
    line.calcType = CALC_TYPES.PROJECTION;
    line.segmentMesh = [new SegmentMesh()];
    line.segmentMaterialId = [-1];
    line.segmentSoilId = [-1];
    line.segConfig = this.createEmptySegConfig(1);
  }

  getLength(line: Line): number {
    return line?.segmentMesh?.reduce((sum, mesh) => sum + mesh.l, 0);
  }

  markToUpdate(usedByLinesWithIds: number[] | null): void {
    if (!this.environmentPropsService.isAnflex) {
      return;
    }
    usedByLinesWithIds?.forEach((lineId) => {
      const line = this.getById(lineId);
      if (line) {
        line.needsUpdate = true;
      }
    });
  }

  markAllToUpdate(): void {
    this.simulation.line.forEach((line) => {
      line.needsUpdate = true;
    });
  }

  hasAnyFlexiblePipe(lineId: number): boolean {
    if (!lineId) {
      return false;
    }

    const line = this.mapsService.getObjectById(lineId) as Line;
    return !!line?.segmentMaterialId.find((id) => {
      return this.mapsService.getTypeById(id) === CONSTANTS.MATERIAL_TYPES.FLEX_LINE;
    });
  }
}
